/**
 * Myers' Diff Algorithm - internal implementation
 * Helper functions for array and string three-way merge
 */

/**
 * Apply a three-way merge on strings using line-based diff
 * @param {string} local - Local string
 * @param {string} master - Master string
 * @param {string} ancestor - Ancestor string
 * @param {boolean} charBased - Use character-based instead of line-based
 * @returns {string} - Merged result
 */
export function applyMyersDiffString(
  local,
  master,
  ancestor,
  charBased = false
) {
  const split = charBased ? s => s.split('') : s => s.split('\n');
  const join = charBased ? a => a.join('') : a => a.join('\n');

  const localLines = split(local);
  const masterLines = split(master);
  const ancestorLines = split(ancestor);

  const result = applyMyersDiff(localLines, masterLines, ancestorLines);
  return join(result);
}

/**
 * Apply a three-way merge on arrays - simplified pragmatic approach
 * @param {Array} local - Local array
 * @param {Array} master - Master array
 * @param {Array} ancestor - Ancestor array
 * @returns {Array} - Merged result
 */
export function applyMyersDiff(local, master, ancestor) {
  // Count occurrences for handling duplicates
  const count = arr => {
    const map = new Map();
    for (const item of arr) {
      map.set(item, (map.get(item) || 0) + 1);
    }
    return map;
  };

  const ancestorCounts = count(ancestor);
  const masterCounts = count(master);
  const localCounts = count(local);

  // Start with master as base
  const result = [...master];

  // Find items deleted in local but kept in master - apply those deletions
  for (const [item, ancestorCount] of ancestorCounts) {
    const localCount = localCounts.get(item) || 0;
    const masterCount = masterCounts.get(item) || 0;

    // Local deleted some/all, master kept them
    if (localCount < ancestorCount && masterCount === ancestorCount) {
      const deleteCount = ancestorCount - localCount;
      for (let i = 0; i < deleteCount; i++) {
        const idx = result.indexOf(item);
        if (idx !== -1) {
          result.splice(idx, 1);
        }
      }
    }
  }

  // Compute net changes to detect pure replacements vs insertions
  // Count total deletions and insertions for each branch
  let totalLocalDeletions = 0;
  let totalMasterDeletions = 0;
  let totalLocalInsertions = 0;
  let totalMasterInsertions = 0;

  for (const [item, ancestorCount] of ancestorCounts) {
    const localCount = localCounts.get(item) || 0;
    const masterCount = masterCounts.get(item) || 0;

    if (localCount < ancestorCount) {
      totalLocalDeletions += ancestorCount - localCount;
    }
    if (masterCount < ancestorCount) {
      totalMasterDeletions += ancestorCount - masterCount;
    }
  }

  for (const [item, localCount] of localCounts) {
    const ancestorCount = ancestorCounts.get(item) || 0;
    if (localCount > ancestorCount) {
      totalLocalInsertions += localCount - ancestorCount;
    }
  }

  for (const [item, masterCount] of masterCounts) {
    const ancestorCount = ancestorCounts.get(item) || 0;
    if (masterCount > ancestorCount) {
      totalMasterInsertions += masterCount - ancestorCount;
    }
  }

  // Check if there's a replacement conflict (both deleted and inserted at same positions)
  const bothDeletedSomething =
    totalLocalDeletions > 0 && totalMasterDeletions > 0;
  const bothInsertedSomething =
    totalLocalInsertions > 0 && totalMasterInsertions > 0;
  const hasReplacementConflict = bothDeletedSomething && bothInsertedSomething;

  // Find items inserted in local - add them if no conflict
  let localInsertionsAdded = 0;
  for (const [item, localCount] of localCounts) {
    const ancestorCount = ancestorCounts.get(item) || 0;
    const masterCount = masterCounts.get(item) || 0;

    if (localCount > ancestorCount) {
      // Local has more of this item than ancestor - it's an insertion
      const localInsertCount = localCount - ancestorCount;

      // If there's a replacement conflict AND master doesn't have this item:
      // - Only skip insertions up to the number of deletions (those are the replacements)
      // - Extra insertions beyond deletions are non-conflicting
      if (hasReplacementConflict && masterCount === 0) {
        const replacementSize = Math.min(
          totalLocalDeletions,
          totalMasterDeletions
        );
        if (localInsertionsAdded < replacementSize) {
          // This insertion is part of the replacement conflict - skip it
          localInsertionsAdded += localInsertCount;
          continue;
        }
      }

      // Non-conflicting insertion - add to result at appropriate position
      const localPos = local.indexOf(item);
      const isNearEnd = localPos >= local.length - localInsertCount;

      for (let i = 0; i < localInsertCount; i++) {
        if (localPos === 0) {
          // Insert at beginning
          result.unshift(item);
        } else if (isNearEnd) {
          // Insert at end if it was at/near end in local
          result.push(item);
        } else {
          // Try to preserve relative position from local
          const insertPos = Math.min(localPos, result.length);
          result.splice(insertPos, 0, item);
        }
      }
      localInsertionsAdded += localInsertCount;
    }
  }

  return result;
}
